import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { NgxPaginationModule } from 'ngx-pagination';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { DeleteConfirmationComponent } from '../../components/delete-confirmation/delete-confirmation.component';
import { Icons } from '../../../shared/helpers/icons';
import { IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { IEvacuationRoute } from '../../../shared/models/evacuationRoute';
import { ISevereWeatherLocation } from '../../../shared/models/severeWeatherLocation';
import { ICrisisStagingAreas } from '../../../shared/models/crisisStagingAreas';
import { IRelocationSites } from '../../../shared/models/relocationSites';
import { EvacuationRouteService } from '../../../shared/services/evacuationRoute.service';
import { SevereWeatherLocationService } from '../../../shared/services/severeWeatherLocation.service';
import { CrisisStagingAreasService } from '../../../shared/services/crisisStagingAreas.service';
import { RelocationSitesService } from '../../../shared/services/relocationSites.service';
import { EvacuationRouteFormComponent } from './evacuation-route-form/evacuation-route-form.component';
import { SevereWeatherFormComponent } from './severe-weather-form/severe-weather-form.component';
import { CorePlanningTeamComponent } from '../../collaborative-planning-team/core-planning-team/core-planning-team.component';
import { ECorePlanningTeamType } from '../../../shared/models/eop_types.enum';

const FIRE = 'Fire';
const BOMB = 'Bomb';
const SEVERE = 'Severe Weather Sheltering Locations';
const CRISIS = 'Crisis Event "Staging" Areas';
const RELOCATION = 'Relocation Sites';
const REUNIFICATION = 'Reunification Team';

@Component({
  selector: 'evacuation-routes-reunification',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    NgxPaginationModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    PrvNextButtonsComponent,
    DeleteConfirmationComponent,
    EvacuationRouteFormComponent,
    SevereWeatherFormComponent,
    CorePlanningTeamComponent
  ],
  templateUrl: './evacuation-routes-reunification.component.html',
  styleUrl: './evacuation-routes-reunification.component.css'
})
export class EvacuationRoutesReunificationComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;

  docs = [FIRE, BOMB, SEVERE, CRISIS, RELOCATION];
  selectedDoc: string = FIRE;

  // The Relocation Sites doc holds two gray tabs: the sites form + the team.
  relocationTabs = [RELOCATION, REUNIFICATION];
  selectedRelocationTab: string = RELOCATION;

  // Reunification Team reuses <core-planning-team> embedded with this team type.
  reunificationTeamType = ECorePlanningTeamType['Reunification Team'];

  // Relocation Sites (single structured form)
  relocation: IRelocationSites = {};
  relocationGrid: { label: string; primary: keyof IRelocationSites; secondary: keyof IRelocationSites }[] = [
    { label: 'Location/Address', primary: 'primary_location_address', secondary: 'secondary_location_address' },
    { label: 'Primary Contact Name(s)/Title(s)', primary: 'primary_contact_name', secondary: 'secondary_contact_name' },
    { label: 'Telephone Number(s)', primary: 'primary_telephone', secondary: 'secondary_telephone' },
  ];

  // Fire / Bomb routes
  routeList: IEvacuationRoute[] = [];
  // Severe weather locations
  severeList: ISevereWeatherLocation[] = [];
  // Crisis staging areas (single form)
  crisis: ICrisisStagingAreas = {};

  crisisSections: { title: string; fields: { key: keyof ICrisisStagingAreas; label: string }[] }[] = [
    {
      title: 'At the School Site',
      fields: [
        { key: 'school_incident_command_post', label: 'Incident Command Post' },
        { key: 'school_media_staging_area', label: 'Media Staging Area' },
        { key: 'school_parent_reunification_location', label: 'Parent Reunification Location' },
        { key: 'school_student_staging_area', label: 'Student Staging Area' },
        { key: 'school_first_aid_treatment_site', label: 'First Aid Treatment Site' },
        { key: 'school_bus_loading_unloading_areas', label: 'Bus Loading/Unloading Areas' },
      ]
    },
    {
      title: 'At the Primary Relocation Site',
      fields: [
        { key: 'primary_incident_command_post', label: 'Incident Command Post' },
        { key: 'primary_media_staging_area', label: 'Media Staging Area' },
        { key: 'primary_parent_reunification_location', label: 'Parent Reunification Location' },
        { key: 'primary_student_staging_area', label: 'Student Staging Area' },
        { key: 'primary_first_aid_treatment_site', label: 'First Aid Treatment Site' },
        { key: 'primary_bus_loading_unloading_areas', label: 'Bus Loading/Unloading Areas' },
      ]
    },
    {
      title: 'At the Secondary Relocation Site',
      fields: [
        { key: 'secondary_incident_command_post', label: 'Incident Command Post' },
        { key: 'secondary_media_staging_area', label: 'Media Staging Area' },
        { key: 'secondary_parent_reunification_location', label: 'Parent Reunification Location' },
        { key: 'secondary_student_staging_area', label: 'Student Staging Area' },
        { key: 'secondary_first_aid_treatment_site', label: 'First Aid Treatment Site' },
        { key: 'secondary_bus_loading_unloading_areas', label: 'Bus Loading/Unloading Areas' },
      ]
    },
  ];

  // Modal / confirmation state
  showRouteForm: boolean = false;
  routeFormItem: IEvacuationRoute | null = null;
  showSevereForm: boolean = false;
  severeFormItem: ISevereWeatherLocation | null = null;
  showDeleteConfirmation: boolean = false;
  deleteItem: IEvacuationRoute | ISevereWeatherLocation | null = null;
  deleteKind: 'route' | 'severe' | null = null;

  // Table sort + pagination
  sortOrder: boolean = true;
  routePage: number = 1;
  severePage: number = 1;
  itemsPerPage: number = 10;

  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string = '';

  constructor(
    private userDataService: UserDataService,
    private evacuationRouteService: EvacuationRouteService,
    private severeWeatherLocationService: SevereWeatherLocationService,
    private crisisStagingAreasService: CrisisStagingAreasService,
    private relocationSitesService: RelocationSitesService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadForSelected();
  }

  // --- doc toggle ---
  selectDoc(doc: string): void {
    this.selectedDoc = doc;
    if (doc === RELOCATION) this.selectedRelocationTab = RELOCATION;
    this.loadForSelected();
  }

  selectRelocationTab(tab: string): void {
    this.selectedRelocationTab = tab;
  }

  get isRouteDoc(): boolean { return this.selectedDoc === FIRE || this.selectedDoc === BOMB; }
  get isSevereDoc(): boolean { return this.selectedDoc === SEVERE; }
  get isCrisisDoc(): boolean { return this.selectedDoc === CRISIS; }
  get isRelocationDoc(): boolean { return this.selectedDoc === RELOCATION; }
  get routeType(): string { return this.selectedDoc === BOMB ? 'bomb' : 'fire'; }

  loadForSelected(): void {
    if (this.isRouteDoc) this.loadRoutes();
    else if (this.isSevereDoc) this.loadSevere();
    else if (this.isCrisisDoc) this.loadCrisis();
    else if (this.isRelocationDoc) this.loadRelocation();
    // Reunification Team self-loads inside the embedded <core-planning-team>.
  }

  // --- Fire / Bomb ---
  loadRoutes(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.routePage = 1;
    this.evacuationRouteService.getEvacuationRoutes(this.currentBuildingId, this.routeType).subscribe({
      next: (response) => {
        this.loading = false;
        this.routeList = response && response.data ? response.data : [];
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  sortRoutes(key: keyof IEvacuationRoute): void {
    this.sortOrder = !this.sortOrder;
    this.routeList.sort((a, b) => {
      const valueA = a[key]?.toString().toLowerCase() || '';
      const valueB = b[key]?.toString().toLowerCase() || '';
      if (valueA < valueB) return this.sortOrder ? -1 : 1;
      if (valueA > valueB) return this.sortOrder ? 1 : -1;
      return 0;
    });
  }

  openAddRoute(): void { this.routeFormItem = null; this.showRouteForm = true; }
  openEditRoute(item: IEvacuationRoute): void { this.routeFormItem = item; this.showRouteForm = true; }
  closeRouteForm(): void { this.showRouteForm = false; this.routeFormItem = null; }
  handleRouteSaved(message: { type: string, message: string }): void {
    this.closeRouteForm();
    this.loadRoutes();
    this.messageModal = message.type;
    this.popupMessage = message.message;
  }

  // --- Severe Weather ---
  loadSevere(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.severePage = 1;
    this.severeWeatherLocationService.getSevereWeatherLocations(this.currentBuildingId).subscribe({
      next: (response) => {
        this.loading = false;
        this.severeList = response && response.data ? response.data : [];
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  openAddSevere(): void { this.severeFormItem = null; this.showSevereForm = true; }
  openEditSevere(item: ISevereWeatherLocation): void { this.severeFormItem = item; this.showSevereForm = true; }
  closeSevereForm(): void { this.showSevereForm = false; this.severeFormItem = null; }
  handleSevereSaved(message: { type: string, message: string }): void {
    this.closeSevereForm();
    this.loadSevere();
    this.messageModal = message.type;
    this.popupMessage = message.message;
  }

  // --- Delete (shared) ---
  openDelete(item: IEvacuationRoute | ISevereWeatherLocation, kind: 'route' | 'severe'): void {
    this.deleteItem = item;
    this.deleteKind = kind;
    this.showDeleteConfirmation = true;
  }
  closeDelete(): void { this.showDeleteConfirmation = false; this.deleteItem = null; this.deleteKind = null; }
  handleDelete(item: IEvacuationRoute | ISevereWeatherLocation): void {
    this.showDeleteConfirmation = false;
    const kind = this.deleteKind;
    if (!this.currentBuildingId || !item?.id) { this.closeDelete(); return; }
    this.loading = true;
    const request$ = kind === 'severe'
      ? this.severeWeatherLocationService.deleteSevereWeatherLocation(this.currentBuildingId, String(item.id))
      : this.evacuationRouteService.deleteEvacuationRoute(this.currentBuildingId, String(item.id));

    request$.subscribe({
      next: () => {
        this.loading = false;
        this.messageModal = 'success';
        this.popupMessage = 'This occurred after deleting an item.';
        if (kind === 'severe') this.loadSevere(); else this.loadRoutes();
        this.deleteItem = null; this.deleteKind = null;
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
        this.deleteItem = null; this.deleteKind = null;
      }
    });
  }

  // --- Crisis staging areas (single form) ---
  loadCrisis(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.crisisStagingAreasService.getCrisisStagingAreas(this.currentBuildingId).subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.has_error) this.crisis = response.data || {};
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  saveCrisis(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.crisisStagingAreasService.updateCrisisStagingAreas(this.currentBuildingId, this.crisis).subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.has_error) {
          this.messageModal = 'success';
          this.popupMessage = 'This occurred after updating Crisis Event Staging Areas.';
          this.loadCrisis();
        } else {
          this.messageModal = 'unsuccess';
          this.popupMessage = response.message;
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  // --- Relocation Sites (single form) ---
  loadRelocation(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.relocationSitesService.getRelocationSites(this.currentBuildingId).subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.has_error) this.relocation = response.data || {};
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  saveRelocation(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.relocationSitesService.updateRelocationSites(this.currentBuildingId, this.relocation).subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.has_error) {
          this.messageModal = 'success';
          this.popupMessage = 'This occurred after updating Relocation Sites.';
          this.loadRelocation();
        } else {
          this.messageModal = 'unsuccess';
          this.popupMessage = response.message;
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }
}
